import FlipMove from 'react-flip-move'
import { LOGGED_USER_VAR } from 'App'
import { SONG_ROLES } from 'const'
import SongMember from 'components/Song/SongEdit/SongEditor/SongMembers/SongMember'
import { SongMemberType } from 'types'
import confirmedAction from 'modals/actions/confirmedAction'
import { forwardRef } from 'react'
import styled from 'styled-components'
import { useReactiveVar } from '@apollo/client'
import { useTranslation } from 'react-i18next'

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;

  & > * {
    background: ${(props) => props.theme.background};
  }
`

const Members = styled.div`
  & > * {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: stretch;
    gap: 15px;
    margin-top: 20px;
  }
`

//
// ANIMATED ITEM (NEEDS TO BE OUTSIDE THE COMPONENTS, OTHERWISE RE-RENDERS AND LOSES INPUT FOCUS ETC.)
//
const AnimatedItem = forwardRef(
  (
    props: {
      member
      deleteMember
      updateMemberRole
      allowUpdateMembers
    },
    ref
  ) => {
    return (
      <div ref={ref as any} style={{ overflowAnchor: 'none' }}>
        <SongMember
          member={props.member}
          deleteMember={props.deleteMember}
          updateMemberRole={props.updateMemberRole}
          allowUpdateMembers={props.allowUpdateMembers}
        />
      </div>
    )
  }
)
/**
 *
 * COMPONENT
 *
 */
const SongMemberList = (props: {
  songMembers: SongMemberType[]
  setSongMembers?: (members: SongMemberType[]) => void
  onDeleteCallback?: () => void
  allowUpdateMembers?: boolean
}) => {
  const { t } = useTranslation()
  const loggedUser = useReactiveVar(LOGGED_USER_VAR)

  //
  // FUNCTIONS
  //
  const membersEqual = (m1: SongMemberType, m2: SongMemberType) => {
    return m1.user?.id === m2.user?.id && m1.userGroup?.id === m2.userGroup?.id
  }

  const getRemainingAdmins = (excludeMember: SongMemberType) => {
    const remainingAdmins = props.songMembers.filter((m) => {
      return (
        !membersEqual(excludeMember, m) && m.songRole?.code === SONG_ROLES.admin
      )
    })
    return remainingAdmins
  }

  //
  // UPDATE MEMBERS
  //
  const updateMember = (updatedMember: SongMemberType) => {
    props.setSongMembers(
      [...props.songMembers].map((m) =>
        membersEqual(m, updatedMember) ? updatedMember : m
      )
    )
  }

  const updateMemberRole = (member: SongMemberType, songRoleCode: string) => {
    updateMember({
      ...member,
      songRole: { code: songRoleCode },
    })
  }

  const safelyUpdateMemberRole = (
    member: SongMemberType,
    songRoleCode: string
  ) => {
    const remainingAdmins = getRemainingAdmins(member)
    if (!remainingAdmins.length && songRoleCode !== SONG_ROLES.admin) {
      confirmedAction({
        title: t('song.members.giving up last admin.title'),
        description: t('song.members.giving up last admin.description'),
        okText: t('song.members.giving up last admin.ok'),
      })
    } else if (
      member.user?.id === loggedUser.id &&
      member.songRole?.code === SONG_ROLES.admin &&
      songRoleCode !== SONG_ROLES.admin
    ) {
      confirmedAction({
        title: t('song.members.giving up admin.title'),
        description: t('song.members.giving up admin.description'),
        okText: t('song.members.giving up admin.yes'),
        cancelText: t('song.members.giving up admin.no'),
        onSubmit: () => updateMemberRole(member, songRoleCode),
      })
    } else {
      updateMemberRole(member, songRoleCode)
    }
  }

  //
  // DELETE MEMBERS
  //
  const deleteMember = (memberToDelete: SongMemberType) => {
    props.setSongMembers(
      [...props.songMembers].filter((m) => !membersEqual(m, memberToDelete))
    )
    props.onDeleteCallback && props.onDeleteCallback()
  }

  const safelyDeleteMember = (memberToDelete: SongMemberType) => {
    const isDeletingSelf = loggedUser?.id === memberToDelete?.user?.id
    const remainingAdmins = getRemainingAdmins(memberToDelete)

    if (isDeletingSelf && !remainingAdmins.length) {
      confirmedAction({
        title: t('song.members.leaving last admin.title'),
        description: t('song.members.leaving last admin.description'),
        okText: t('song.members.leaving last admin.ok'),
      })
    } else if (isDeletingSelf) {
      confirmedAction({
        title: t('song.members.leaving.title'),
        description: t('song.members.leaving.description'),
        okText: t('song.members.leaving.yes'),
        cancelText: t('song.members.leaving.no'),
        onSubmit: () => deleteMember(memberToDelete),
      })
    } else if (!remainingAdmins.length) {
      confirmedAction({
        title: t('song.members.removing last admin.title'),
        description: t('song.members.removing last admin.description').replace(
          '{USER/GROUP}',
          memberToDelete?.user?.name || memberToDelete?.userGroup?.name
        ),
        okText: t('song.members.removing last admin.ok'),
      })
    } else {
      confirmedAction({
        title: t('song.members.removing member.title'),
        description: t('song.members.removing member.description').replace(
          '{USER/GROUP}',
          memberToDelete?.user?.name || memberToDelete?.userGroup?.name
        ),
        okText: t('song.members.removing member.yes'),
        cancelText: t('song.members.removing member.no'),
        onSubmit: () => deleteMember(memberToDelete),
      })
    }
  }

  //
  // RENDER
  //
  return (
    <Wrapper>
      <Members>
        <FlipMove
          easing="ease"
          duration={200} // milliseconds
          staggerDurationBy={80} // milliseconds
          staggerDelayBy={100} // milliseconds
          maintainContainerHeight={false}
          appearAnimation={{
            /* first item appearance */
            from: {
              opacity: '0',
              transform: 'scale(0.4)',
              transformOrigin: '50% 50%',
            },
            to: { opacity: '1', transform: 'none' },
          }}
        >
          {props.songMembers?.map((member: SongMemberType) => (
            <AnimatedItem
              key={member.user?.id || `gr_${member.userGroup?.id}`}
              member={member}
              updateMemberRole={safelyUpdateMemberRole}
              deleteMember={safelyDeleteMember}
              allowUpdateMembers={props.allowUpdateMembers}
            />
          ))}
        </FlipMove>
      </Members>
    </Wrapper>
  )
}

export default SongMemberList
